import { useRef, useState, useEffect, useMemo } from 'react';
import type { Node } from '~/types/file-tree';

export function useCollapsedFolders(
  fileList: Node[],
  collapsed: boolean,
  selectedFile: string | undefined,
  preserveCollapsedState: boolean,
) {
  const prevFileList = useRef<typeof fileList>();

  const [collapsedFolders, setCollapsedFolders] = useState(() => {
    return collapsed
      ? new Set(fileList.filter((item) => item.kind === 'folder').map((item) => item.fullPath))
      : new Set<string>();
  });

  useEffect(() => {
    if (collapsed && !preserveCollapsedState) {
      setCollapsedFolders(new Set(fileList.filter((item) => item.kind === 'folder').map((item) => item.fullPath)));
      return;
    }

    setCollapsedFolders((prevCollapsed) => {
      const newCollapsed = new Set<string>();

      const existingFolders = new Set<string>(
        prevFileList.current?.filter((item) => item.kind === 'folder').map((item) => item.fullPath),
      );

      for (const folder of fileList) {
        if (folder.kind === 'folder') {
          const partOfSelectedFilePath = selectedFile?.startsWith(folder.fullPath + '/');

          if (partOfSelectedFilePath) {
            continue;
          }

          if (prevCollapsed.has(folder.fullPath)) {
            newCollapsed.add(folder.fullPath);
          } else if (collapsed && !existingFolders.has(folder.fullPath)) {
            newCollapsed.add(folder.fullPath);
          }
        }
      }

      return newCollapsed;
    });
  }, [fileList, collapsed, selectedFile, preserveCollapsedState]);

  const filteredFileList = useMemo(() => {
    const list = [];

    let lastDepth = Number.MAX_SAFE_INTEGER;

    for (const fileOrFolder of fileList) {
      const depth = fileOrFolder.depth;

      // if the depth is equal we reached the end of the collaped group
      if (lastDepth === depth) {
        lastDepth = Number.MAX_SAFE_INTEGER;
      }

      // ignore collapsed folders
      if (collapsedFolders.has(fileOrFolder.fullPath)) {
        lastDepth = Math.min(lastDepth, depth);
      }

      // ignore files and folders below the last collapsed folder
      if (lastDepth < depth) {
        continue;
      }

      list.push(fileOrFolder);
    }

    return list;
  }, [fileList, collapsedFolders]);

  // this effect must be last to get the correct value everywhere else
  useEffect(() => {
    prevFileList.current = fileList;
  }, [fileList]);

  const toggleCollapseState = (fullPath: string) => {
    setCollapsedFolders((prevSet) => {
      const newSet = new Set(prevSet);

      if (newSet.has(fullPath)) {
        newSet.delete(fullPath);
      } else {
        newSet.add(fullPath);
      }

      return newSet;
    });
  };

  return {
    collapsedFolders,
    filteredFileList,
    toggleCollapseState,
  };
}
